import { useState } from 'react';
import { Loader2, Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import api from '@/lib/api';
import { AudioRecorder } from '../components/AudioRecorder';
import { useTranscription } from '../hooks/useTranscription';

async function uploadAudioFile(file: File): Promise<string> {
  const memberships = await api.listMyVaultMemberships();
  if (!memberships.personalHolder) {
    throw new Error('Personal workspace not provisioned');
  }
  const doc = await api.uploadFileToVault(file, memberships.personalHolder.holderId, {
    tags: ['voice-studio', 'recording'],
  });
  return doc.documentId;
}

export function TranscriptionPage() {
  const { transcribe, transcribing, result, error } = useTranscription();
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleRecorded = async (file: File) => {
    setUploading(true);
    try {
      const fileId = await uploadAudioFile(file);
      await transcribe(fileId);
    } finally {
      setUploading(false);
    }
  };

  const handleCopy = () => {
    if (!result?.text) return;
    navigator.clipboard.writeText(result.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isWorking = uploading || transcribing;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Speech-to-Text</h1>
        <p className="text-muted-foreground mt-1">
          Record audio and convert it to text using OpenAI Whisper.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Record Audio</CardTitle>
          <CardDescription>
            Click &quot;Start Recording&quot;, speak, then stop. The audio will be transcribed
            automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AudioRecorder onRecorded={handleRecorded} disabled={isWorking} />
        </CardContent>
      </Card>

      {isWorking && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {uploading ? 'Uploading audio…' : 'Transcribing…'}
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {result && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Transcription</CardTitle>
              <Button variant="ghost" size="sm" onClick={handleCopy} className="gap-1.5">
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            {result.detectedLanguage && (
              <CardDescription>
                Language: {result.detectedLanguage}
                {result.durationSeconds != null && ` · ${result.durationSeconds.toFixed(1)}s`}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <Textarea value={result.text} readOnly rows={6} className="resize-none font-normal" />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
